/**
 * Loading placeholders.
 *
 * Two rules decide when these appear, because a skeleton shown carelessly is
 * worse than none:
 *
 *  1. Only after a delay. Most loads finish in tens of milliseconds; a skeleton
 *     that flashes for 80ms reads as a glitch, not as progress. It appears only
 *     if the wait outlasts the threshold, so a fast screen never shows one.
 *  2. Only when there is nothing to show. A background refresh (an SSE change
 *     event, a filter re-fetch) keeps the current data on screen — replacing
 *     real content with grey boxes throws away information the user was reading.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigation } from 'react-router';

/**
 * Whether the working scope is being changed right now.
 *
 * A revalidation normally must NOT show placeholders - the data on screen is
 * still true, and replacing it with grey boxes throws away what someone was
 * reading. A scope change is the exception that proves the rule: every figure on
 * screen belongs to the clinic or store being switched away from, so continuing
 * to show it is showing the wrong tenant's numbers as though they were the new
 * one's.
 */
const ScopeSwitch = createContext(false);

export function ScopeSwitchProvider({
  switching,
  children,
}: {
  switching: boolean;
  children: ReactNode;
}) {
  return <ScopeSwitch.Provider value={switching}>{children}</ScopeSwitch.Provider>;
}

/**
 * True only once `active` has stayed true for `delayMs`.
 *
 * The trailing `minVisibleMs` matters as much as the delay: once a skeleton is
 * on screen it stays briefly, otherwise a load that finishes just past the
 * threshold produces a flicker — exactly the artefact the delay was avoiding.
 */
export function useDelayedFlag(active: boolean, delayMs = 350, minVisibleMs = 300): boolean {
  const [visible, setVisible] = useState(false);
  const [shownAt, setShownAt] = useState<number | null>(null);

  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => {
        setVisible(true);
        setShownAt(Date.now());
      }, delayMs);
      return () => clearTimeout(timer);
    }

    if (!visible) return;
    const elapsed = shownAt ? Date.now() - shownAt : minVisibleMs;
    const remaining = Math.max(0, minVisibleMs - elapsed);
    const timer = setTimeout(() => {
      setVisible(false);
      setShownAt(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [active, delayMs, minVisibleMs, visible, shownAt]);

  return visible;
}

/**
 * Whether to show placeholders in place of this screen's content.
 *
 * True only while navigating to a *different* route and the wait has outlasted
 * the delay. A revalidation of the current route returns false: that data is
 * already on screen and must not be replaced with grey boxes.
 */
export function usePendingScreen(delayMs = 350): boolean {
  const navigation = useNavigation();
  const location = useLocation();
  const switchingScope = useContext(ScopeSwitch);
  const movingElsewhere =
    navigation.state === 'loading' &&
    navigation.location !== undefined &&
    navigation.location.pathname !== location.pathname;
  // A shorter delay for a scope change: it follows a deliberate click, so the
  // wait is expected and there is no risk of a placeholder reading as a glitch.
  return useDelayedFlag(movingElsewhere || switchingScope, switchingScope ? 120 : delayMs);
}

/** A single shimmering block. `aria-hidden`: the region announces the loading. */
export function SkeletonBlock({
  width = '100%',
  height = 12,
}: {
  width?: string;
  height?: number;
}) {
  return <span className="skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** Matches the stat tile's shape, so the layout does not jump when data lands. */
export function SkeletonTile() {
  return (
    <div className="stat" aria-hidden="true">
      <div className="stat__head">
        <span className="stat__icon skeleton skeleton--icon" />
        <SkeletonBlock width="70%" height={11} />
      </div>
      <div style={{ margin: '6px 0 4px' }}>
        <SkeletonBlock width="45%" height={24} />
      </div>
      <SkeletonBlock width="35%" height={10} />
    </div>
  );
}

export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    // One live region for the whole group: a screen reader hears "読み込み中"
    // once, not once per tile.
    <div className="grid grid--4" role="status" aria-live="polite" aria-label="読み込み中">
      {Array.from({ length: count }, (_, index) => (
        <SkeletonTile key={index} />
      ))}
    </div>
  );
}

/**
 * The people grids, while they load.
 *
 * Shaped like a `.ptile` rather than like a generic card: the face is a circle,
 * the name is short and centred, the description is two lines. A placeholder
 * whose shape does not match what follows it moves the whole grid when the data
 * lands, which is the jump these exist to prevent.
 */
export function SkeletonPeople({ count = 4 }: { count?: number }) {
  return (
    <div className="pgrid" role="status" aria-live="polite" aria-label="読み込み中">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="ptile ptile--loading" aria-hidden="true">
          <span className="skeleton skeleton--face" />
          <SkeletonBlock width="60%" height={13} />
          <SkeletonBlock width="40%" height={11} />
          <SkeletonBlock width="90%" height={10} />
          <SkeletonBlock width="50%" height={10} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-live="polite" aria-label="読み込み中">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="skeleton-row">
          {Array.from({ length: columns }, (_, column) => (
            <SkeletonBlock key={column} width={column === 0 ? '60%' : '40%'} height={11} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Cold start: the shell is real, the content is not there yet. */
export function AppSkeleton() {
  return (
    <div className="shell">
      <nav className="sidebar" aria-hidden="true">
        <div className="sidebar__brand">記事咲</div>
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} style={{ padding: '8px 12px' }}>
            <SkeletonBlock width={`${60 + ((index * 7) % 30)}%`} height={12} />
          </div>
        ))}
      </nav>
      <div className="main">
        <div className="statusbar" aria-hidden="true">
          <SkeletonBlock width="180px" height={14} />
        </div>
        <main className="content">
          <div className="page">
            <section className="card">
              <SkeletonTiles />
            </section>
            <section className="card">
              <SkeletonRows />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
